/**
 * Canonical user preference schema.
 *
 * Single source of truth for preference field names + valid values across
 * web, mobile, and backend. Replaces the multi-decade drift between
 * `tripPace` (web), `pace` (mobile/backend), `preferredPace` (web again),
 * and the 33 legacy snake_case columns on `user_profiles`.
 *
 * Storage: `user_profiles.preferences` JSONB (one canonical blob per user).
 * Schema migration: see migration `add_user_profiles_preferences_jsonb`.
 *
 * No zod dependency — kept dependency-free so mobile/web bundles don't grow.
 * The validation API mirrors zod's ergonomics:
 *   - `validatePreferences(input)` → { valid, data, errors }
 *   - `normalizePreferences(input)` → canonical-only fields
 *   - `applyDefaults(prefs)` → fully-populated object
 *
 * Used by:
 *   - userProfileSync.js          (load/save through this)
 *   - generationOrchestrator.js   (reads canonical fields only)
 *   - userMessageBuilder.js       (reads canonical fields only)
 *   - PersonalizationModal.jsx    (web)
 *   - SettingsScreen.js           (mobile)
 */

// ─── Enums ──────────────────────────────────────────────────────────────────

export const PACE_VALUES = ['relaxed', 'moderate', 'packed'];
export const BUDGET_VALUES = ['budget', 'moderate', 'luxury', 'ultra-luxury', 'custom'];
export const HOTEL_TIER_VALUES = ['budget', 'mid-range', 'luxury', 'ultra-luxury'];
export const CABIN_CLASS_VALUES = ['ECONOMY', 'PREMIUM_ECONOMY', 'BUSINESS', 'FIRST'];
export const ACTIVITY_LEVEL_VALUES = ['low', 'moderate', 'high'];

// Companion types — multi-select array. Split into "base" (mutually exclusive
// at the UI layer — you're either solo OR a couple OR a family) and
// "modifiers" (additive — you can be a couple WITH kids AND elderly).
// Storage is a single array; UI components enforce the base-exclusivity rule.
export const COMPANION_BASE_VALUES = ['solo', 'couple', 'family', 'friends', 'group', 'business'];
export const COMPANION_MODIFIER_VALUES = ['kids', 'teens', 'elderly', 'wheelchair', 'pets'];
export const COMPANION_VALUES = [...COMPANION_BASE_VALUES, ...COMPANION_MODIFIER_VALUES];

// ─── Field definitions ──────────────────────────────────────────────────────
// Each field declares: type, default, validator (optional), description.

const FIELD_TYPES = {
  // Travel style — used by orchestrator + userMessageBuilder
  pace: { type: 'enum', values: PACE_VALUES, default: 'moderate' },
  budget: { type: 'enum', values: BUDGET_VALUES, default: 'moderate' },
  hotelTier: { type: 'enum', values: HOTEL_TIER_VALUES, default: 'mid-range' },
  cabinClass: { type: 'enum', values: CABIN_CLASS_VALUES, default: 'ECONOMY' },
  activityLevel: { type: 'enum', values: ACTIVITY_LEVEL_VALUES, default: 'moderate' },
  // companions: array of allowed values. Legacy single-string writes are
  // coerced to single-element array by the normalizer.
  companions: { type: 'string[]', values: COMPANION_VALUES, default: [] },

  // Personalization arrays
  interests: { type: 'string[]', default: [] },
  travelStyles: { type: 'string[]', default: [] },
  dietary: { type: 'string[]', default: [] },
  accessibilityNeeds: { type: 'string[]', default: [] },

  // Likes / dislikes
  likedActivities: { type: 'string[]', default: [] },
  dislikedActivities: { type: 'string[]', default: [] },
  likedCuisines: { type: 'string[]', default: [] },
  dislikedCuisines: { type: 'string[]', default: [] },
  likedPlaces: { type: 'string[]', default: [] },     // dream/loved destinations
  avoidPlaces: { type: 'string[]', default: [] },     // disliked destinations
  bucketList: { type: 'string[]', default: [] },
  pastPlaces: { type: 'string[]', default: [] },

  // Travel context
  homeCity: { type: 'string', default: '' },
  passportCountry: { type: 'string', default: '' },
  homeAirports: { type: 'string[]', default: [] },
  currency: { type: 'string', default: 'USD' },
  defaultTripLength: { type: 'integer', min: 1, max: 60, default: null },
  customBudgetAmount: { type: 'integer', min: 0, max: 1000000, default: null },

  // Hotel preferences
  preferredHotelStyles: { type: 'string[]', default: [] },
  preferredNeighborhoods: { type: 'string[]', default: [] },

  // Transit preferences
  preferredTransitModes: { type: 'string[]', default: [] },

  // Schedule preferences
  preferredTimes: { type: 'string[]', default: [] },
  avoidedTimes: { type: 'string[]', default: [] },

  // Notes
  safetyConsiderations: { type: 'string', default: '' },
  travelNotes: { type: 'string', default: '' },
};

// ─── Legacy field aliases ───────────────────────────────────────────────────
// Maps any historical/platform-specific field names → canonical name.
// The normalizer applies these BEFORE validation, so legacy data survives
// migration AND incoming requests with old names auto-correct.

export const LEGACY_FIELD_MAP = {
  // Web variations on pace
  tripPace: 'pace',
  preferredPace: 'pace',

  // Snake-case from dropped columns
  travel_pace: 'pace',
  default_budget: 'budget',
  travel_style: 'travelStyles',
  travel_styles: 'travelStyles',
  dietary_restrictions: 'dietary',
  accessibility_needs: 'accessibilityNeeds',
  preferred_hotel_tier: 'hotelTier',
  default_trip_length: 'defaultTripLength',
  liked_activities: 'likedActivities',
  disliked_activities: 'dislikedActivities',
  liked_cuisines: 'likedCuisines',
  disliked_cuisines: 'dislikedCuisines',
  preferred_neighborhoods: 'preferredNeighborhoods',
  preferred_hotel_styles: 'preferredHotelStyles',
  preferred_transit_modes: 'preferredTransitModes',
  preferred_times: 'preferredTimes',
  avoided_times: 'avoidedTimes',
  travel_companions: 'companions',
  safety_considerations: 'safetyConsiderations',
  safety_notes: 'safetyConsiderations',
  travel_notes: 'travelNotes',
  home_city: 'homeCity',
  passport_country: 'passportCountry',
  home_airports: 'homeAirports',
  preferred_cabin_class: 'cabinClass',
  bucket_list_destinations: 'bucketList',
  dream_destinations: 'likedPlaces',
  loved_places: 'likedPlaces',
  favorite_destinations: 'likedPlaces',
  avoid_places: 'avoidPlaces',
  least_favorite_destinations: 'avoidPlaces',
  past_places: 'pastPlaces',

  // Companion variations from web
  travel_companion: 'companions',
  companion: 'companions',

  // Activity level variations
  activity_level: 'activityLevel',
  custom_budget_amount: 'customBudgetAmount',
};

// ─── PUBLIC API ─────────────────────────────────────────────────────────────

/**
 * The canonical default preferences object. Use as the seed for new profiles
 * or when a field is missing during reads.
 */
export const PREFERENCE_DEFAULTS = Object.freeze(
  Object.fromEntries(Object.entries(FIELD_TYPES).map(([k, v]) => [k, v.default])),
);

/**
 * Canonical field names (sorted). Useful for tests and debugging.
 */
export const CANONICAL_FIELDS = Object.freeze(Object.keys(FIELD_TYPES).sort());

/**
 * Take any object with mixed legacy/canonical keys and return one with ONLY
 * canonical keys. Unknown keys are dropped.
 *
 *   normalizePreferences({ tripPace: 'relaxed', pace: 'packed' })
 *   // → { pace: 'packed' }   (canonical wins on conflict)
 */
export function normalizePreferences(input) {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {};
  const out = {};

  // First pass: legacy fields → canonical (canonical wins if both exist)
  for (const [key, value] of Object.entries(input)) {
    if (value === undefined) continue;
    if (FIELD_TYPES[key]) {
      // Already canonical
      out[key] = value;
    } else if (LEGACY_FIELD_MAP[key]) {
      const canonical = LEGACY_FIELD_MAP[key];
      // Don't overwrite if canonical was already set (canonical wins)
      if (!(canonical in out)) {
        out[canonical] = value;
      }
    }
    // else: unknown field, drop silently
  }

  // Type coercions — handles cross-version data (e.g. companions used to be
  // a single string, now it's an array; users with old profiles should not
  // see a validation error).
  if ('companions' in out) {
    out.companions = coerceCompanionsToArray(out.companions);
  }

  return out;
}

/**
 * Companions used to be a single enum value ('solo'|'couple'|...). It's now
 * a multi-select array. This helper accepts both shapes and returns an array.
 *
 *   coerceCompanionsToArray('couple')       → ['couple']
 *   coerceCompanionsToArray(['couple','kids']) → ['couple','kids']
 *   coerceCompanionsToArray(null)           → []
 *   coerceCompanionsToArray('couple, kids') → ['couple','kids']
 */
export function coerceCompanionsToArray(value) {
  if (Array.isArray(value)) {
    return value
      .filter(x => typeof x === 'string')
      .map(x => x.trim())
      .filter(x => x.length > 0);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    // Tolerate comma-separated strings ('couple, kids')
    if (trimmed.includes(',')) {
      return trimmed.split(',').map(s => s.trim()).filter(Boolean);
    }
    return [trimmed];
  }
  return [];
}

/**
 * Render companions as a human-readable string for LLM prompts.
 *
 *   companionsToPromptString(['couple','kids'])     → 'couple with kids'
 *   companionsToPromptString(['family','elderly'])  → 'family with elderly'
 *   companionsToPromptString(['solo'])              → 'solo'
 *   companionsToPromptString([])                    → 'solo'  (default fallback)
 */
export function companionsToPromptString(value, fallback = 'solo') {
  const arr = coerceCompanionsToArray(value);
  if (arr.length === 0) return fallback;
  const base = arr.find(v => COMPANION_BASE_VALUES.includes(v));
  const modifiers = arr.filter(v => COMPANION_MODIFIER_VALUES.includes(v));
  if (base && modifiers.length > 0) {
    return `${base} with ${modifiers.join(' and ')}`;
  }
  if (base) return base;
  if (modifiers.length > 0) return `traveling with ${modifiers.join(' and ')}`;
  return arr.join(', ');
}

/**
 * Validate a preferences object. Coerces types where safe, enforces enums,
 * drops invalid values to default.
 *
 * @returns {{valid: boolean, data: object, errors: Array<{field, message}>}}
 */
export function validatePreferences(input) {
  const normalized = normalizePreferences(input);
  const data = {};
  const errors = [];

  for (const [field, def] of Object.entries(FIELD_TYPES)) {
    if (!(field in normalized)) continue;
    const raw = normalized[field];

    if (raw === null) {
      data[field] = null;
      continue;
    }

    switch (def.type) {
      case 'enum': {
        if (typeof raw !== 'string') {
          errors.push({ field, message: `expected string, got ${typeof raw}` });
        } else if (!def.values.includes(raw)) {
          errors.push({ field, message: `'${raw}' not in [${def.values.join(', ')}]` });
        } else {
          data[field] = raw;
        }
        break;
      }
      case 'string': {
        if (typeof raw !== 'string') {
          errors.push({ field, message: `expected string, got ${typeof raw}` });
        } else {
          data[field] = raw;
        }
        break;
      }
      case 'integer': {
        const n = typeof raw === 'number' ? raw : parseInt(raw, 10);
        if (!Number.isFinite(n) || !Number.isInteger(n)) {
          errors.push({ field, message: `expected integer, got ${raw}` });
        } else if (def.min != null && n < def.min) {
          errors.push({ field, message: `${n} < min ${def.min}` });
        } else if (def.max != null && n > def.max) {
          errors.push({ field, message: `${n} > max ${def.max}` });
        } else {
          data[field] = n;
        }
        break;
      }
      case 'string[]': {
        if (!Array.isArray(raw)) {
          errors.push({ field, message: `expected array, got ${typeof raw}` });
        } else {
          // Filter to strings; drop empty + whitespace-only entries.
          // Real data: users sometimes have stale '  ' in legacy arrays.
          let filtered = raw.filter(x => typeof x === 'string' && x.trim().length > 0);
          // If the field declares an allowed `values` whitelist, drop unknown values
          // (don't error — old clients may have written deprecated tokens).
          if (Array.isArray(def.values)) {
            filtered = filtered.filter(x => def.values.includes(x));
          }
          // De-dupe (e.g. legacy + new write merged through normalizer)
          data[field] = Array.from(new Set(filtered));
        }
        break;
      }
      default:
        errors.push({ field, message: `unknown type ${def.type}` });
    }
  }

  return {
    valid: errors.length === 0,
    data,
    errors,
  };
}

/**
 * Returns a fully-populated preferences object: stored values OR defaults
 * for any missing field. Use this when reading prefs to ensure callers
 * never get `undefined` for canonical fields.
 */
export function applyDefaults(prefs) {
  const validated = validatePreferences(prefs || {});
  return { ...PREFERENCE_DEFAULTS, ...validated.data };
}

/**
 * Produce a sanitized preferences object suitable for saving to JSONB.
 * - Normalizes legacy keys
 * - Strips unknown keys
 * - Validates enum/types (drops invalid fields to default)
 * - Returns only fields that differ from default (smaller JSONB)
 *
 * Use on the WRITE path to keep storage compact + canonical.
 */
export function preparePreferencesForSave(input) {
  const { data } = validatePreferences(input);
  // Strip fields equal to default to keep JSONB compact
  const out = {};
  for (const [key, value] of Object.entries(data)) {
    const def = FIELD_TYPES[key]?.default;
    if (Array.isArray(def) && Array.isArray(value)) {
      if (value.length > 0) out[key] = value;
    } else if (value !== def && value !== null && value !== '') {
      out[key] = value;
    }
  }
  return out;
}

/**
 * Diff two preferences objects, returning fields that differ.
 * Useful for audit logging + minimal sync payloads.
 */
export function diffPreferences(a, b) {
  const diff = {};
  const aNorm = applyDefaults(a);
  const bNorm = applyDefaults(b);
  for (const key of CANONICAL_FIELDS) {
    const av = aNorm[key];
    const bv = bNorm[key];
    if (Array.isArray(av) && Array.isArray(bv)) {
      if (av.length !== bv.length || av.some((x, i) => x !== bv[i])) {
        diff[key] = { from: av, to: bv };
      }
    } else if (av !== bv) {
      diff[key] = { from: av, to: bv };
    }
  }
  return diff;
}
